import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SELF = ["phone", "address", "emergency_name", "emergency_phone"];
const FIELDS = [
  { k: "job_title", l: "Job title" }, { k: "start_date", l: "Start date", t: "date" }, { k: "date_of_birth", l: "Date of birth", t: "date" },
  { k: "phone", l: "Phone", t: "tel" }, { k: "address", l: "Address", full: true }, { k: "emergency_name", l: "Emergency contact name" },
  { k: "emergency_phone", l: "Emergency contact phone", t: "tel" },
];

export default function ProfileForm({ person, hr, onSaved }) {
  const [p, setP] = useState({});
  const [ent, setEnt] = useState(20);
  useEffect(() => { setP(person.profile || {}); setEnt(person.leave_entitlement); }, [person]);
  const can = (k) => hr || SELF.includes(k);
  const save = async () => {
    const body = hr ? { ...p, leave_entitlement: ent } : Object.fromEntries(SELF.map((k) => [k, p[k] || ""]));
    try { await api.patch(`/hr/people/${person.id}`, body); toast.success("Profile saved"); onSaved?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="card-industrial p-5" data-testid="profile-form">
      <div className="grid sm:grid-cols-3 gap-3 mb-4 text-sm">
        <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Name</div><div className="font-bold">{person.name}</div></div>
        <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Role</div><div>{ROLE_LABELS[person.role]}</div></div>
        <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Site</div><div>{person.home_location}</div></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <div key={f.k} className={f.full ? "sm:col-span-2" : ""}>
            <Label className="text-xs">{f.l}</Label>
            <Input type={f.t || "text"} value={p[f.k] || ""} disabled={!can(f.k)} onChange={(e) => setP({ ...p, [f.k]: e.target.value })} data-testid={`profile-${f.k}`} className="mt-1 h-11" />
          </div>
        ))}
        <div>
          <Label className="text-xs">Employment type</Label>
          <Select value={p.employment_type || ""} onValueChange={(v) => setP({ ...p, employment_type: v })} disabled={!hr}>
            <SelectTrigger data-testid="profile-employment_type" className="mt-1 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{["full-time", "part-time", "apprentice"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {hr && <div><Label className="text-xs">Annual leave entitlement (days)</Label>
          <Input type="number" step="0.5" value={ent} onChange={(e) => setEnt(e.target.value)} data-testid="profile-entitlement" className="mt-1 h-11" /></div>}
        {hr && <div className="sm:col-span-2"><Label className="text-xs">Notes</Label>
          <Textarea rows={2} value={p.notes || ""} onChange={(e) => setP({ ...p, notes: e.target.value })} data-testid="profile-notes" className="mt-1" /></div>}
      </div>
      {!hr && <p className="text-[11px] text-muted-foreground mt-3">You can update your phone, address and emergency contact. Ask the office to change anything else.</p>}
      <Button onClick={save} data-testid="profile-save" className="mt-4 h-11 font-bold uppercase">Save profile</Button>
    </div>
  );
}
